import React, { useEffect } from 'react';
import { useTenant } from '../../contexts/TenantContext';

/**
 * TcnThemeProvider — toggles `<html data-theme="tcn">` while mounted under a
 * TCN tenant context. Pass-through (renders children, no UI).
 *
 * Behavior:
 *  - If `useTenant().tenantSlug === 'tcn'` (or `forceTcn` prop is true) →
 *    sets `document.documentElement.setAttribute('data-theme', 'tcn')` on mount.
 *  - On unmount or when the tenant changes away from TCN, removes the attribute
 *    so other parts of the app fall back to the NoiRock light theme.
 *
 * Why a provider at all? It lets us scope TCN visuals to the `/tcn/*` subtree
 * without polluting the global root. A page that renders <TcnThemeProvider>
 * inside its tree (e.g. /tcn/login, /tcn/me) will flip the theme even when the
 * user has not yet authenticated, by using `forceTcn`.
 */
const TcnThemeProvider = ({ forceTcn = false, children }) => {
  // useTenant throws when there's no provider — guard for the unauth case
  // (e.g. /tcn/login mounted before AuthContext has resolved).
  let tenantSlug = null;
  try {
    tenantSlug = useTenant().tenantSlug;
  } catch (e) {
    tenantSlug = null;
  }

  const shouldApply = forceTcn || tenantSlug === 'tcn';

  useEffect(() => {
    if (!shouldApply) return undefined;
    const prev = document.documentElement.getAttribute('data-theme');
    document.documentElement.setAttribute('data-theme', 'tcn');
    return () => {
      // Only revert if we set it (don't clobber another theme set after us)
      if (document.documentElement.getAttribute('data-theme') === 'tcn') {
        if (prev) {
          document.documentElement.setAttribute('data-theme', prev);
        } else {
          document.documentElement.removeAttribute('data-theme');
        }
      }
    };
  }, [shouldApply]);

  return <>{children}</>;
};

export default TcnThemeProvider;
